import React, { memo, useEffect, useRef, useState } from 'react';
import API_BASE_URL from '../../../../js/urlHelper';
import { fetchWithAuth } from '../../../../js/authToken';
import { toast } from 'react-toastify';

const PrestamoSearch = ({ onSelectPrestamo, onClear, apiBasePath = '/api/admin' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [prestamos, setPrestamos] = useState([]);
  const [selectedPrestamo, setSelectedPrestamo] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim().length >= 1) {
        fetchPrestamos(searchTerm.trim());
      } else {
        setPrestamos([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchPrestamos = async (search = '') => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}${apiBasePath}/prestamos?search=${search}`);
      const data = await response.json();
      if (data.success && data.data.data) {
        setPrestamos(data.data.data);
        setShowDropdown(search.length > 0 && !selectedPrestamo);
      } else {
        setPrestamos([]);
        setShowDropdown(false);
        toast.error('No se encontraron préstamos');
      }
    } catch (error) {
      console.error('Error fetching prestamos:', error);
      toast.error('Error al buscar préstamos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPrestamo = (prestamo) => {
    setSelectedPrestamo(prestamo);
    setSearchTerm(prestamo.idPrestamo.toString());
    setShowDropdown(false);
    onSelectPrestamo(prestamo);
  };


  const handleClearSelection = () => {
    setSearchTerm('');
    setSelectedPrestamo(null);
    setShowDropdown(false);
    setPrestamos([]);
    if (onClear) onClear();
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value === '') {
      handleClearSelection();
    }
  };

  const handleInputFocus = () => {
    if (!selectedPrestamo && (prestamos.length > 0 || searchTerm.length > 0)) {
      setShowDropdown(true);
    }
  };
  
  return (
    <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3 md:mb-6 md:p-4">
      <label className="flex w-full flex-col text-sm font-medium text-slate-700 sm:text-base">
        BUSCAR PRÉSTAMO A EDITAR 
        <div className="relative mt-1 md:mt-2" ref={dropdownRef}>
          <div className="flex items-center">
            <input
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
              placeholder="INGRESE ID DE PRÉSTAMO..."
              disabled={!!selectedPrestamo}
            />
            {isLoading && (
              <div className="absolute right-3 top-2.5">
                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-red-500"></div>
              </div>
            )}
            {selectedPrestamo && (
              <button
                type="button"
                onClick={handleClearSelection}
                className="absolute right-3 top-2.5 text-slate-500 transition hover:text-slate-700"
                aria-label="Clear selection"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>

          {showDropdown && prestamos.length > 0 && (
            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 text-base shadow-lg focus:outline-none sm:text-sm">
              {prestamos.map((prestamo) => (
                <div
                  key={prestamo.idPrestamo}
                  className="cursor-pointer px-4 py-2 transition hover:bg-red-50"
                  onClick={() => handleSelectPrestamo(prestamo)}
                >
                  <div className="font-medium">
                    ID Préstamo: {prestamo.idPrestamo}
                  </div>
                  <div className="text-sm text-slate-500">
                    Cliente: {prestamo.cliente.datos.nombre} {prestamo.cliente.datos.apellidoPaterno} {prestamo.cliente.datos.apellidoMaterno}
                    <br />
                    Fecha de Generación: {prestamo.fecha_generacion}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showDropdown && prestamos.length === 0 && !isLoading && (
            <div className="absolute z-10 mt-1 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 text-base shadow-lg focus:outline-none sm:text-sm">
              <div className="px-4 py-2 text-slate-500">NO SE ENCONTRARON PRÉSTAMOS</div>
            </div>
          )}
        </div>

        {selectedPrestamo && (
          <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-medium text-slate-800">
                  Préstamo ID: {selectedPrestamo.idPrestamo}
                </h4>
                <div className="mt-1 text-sm text-slate-600">
                  <p>
                    Cliente: {selectedPrestamo.cliente.datos.nombre}{' '}
                    {selectedPrestamo.cliente.datos.apellidoPaterno}{' '}
                    {selectedPrestamo.cliente.datos.apellidoMaterno}{' '}
                    {selectedPrestamo.cliente.datos.apellidoConyuge || ''}
                  </p>
                    <p>DNI: {selectedPrestamo.cliente.datos.dni}</p>
                    <p>Monto: S/ {selectedPrestamo.monto}</p>
                    <p>Hora cobranza: {selectedPrestamo.hora_cobranza || 'Sin registrar'}</p>
                    <p>Fecha Generación: {selectedPrestamo.fecha_generacion}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleClearSelection}
                className="text-slate-500 transition hover:text-slate-700"
                aria-label="Clear selection"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </label>
    </div>
  );
};

export default memo(PrestamoSearch);